import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Slider } from "../components/ui/slider";

const TIER_ORDER = ["starter", "pro", "enterprise"];
const TIER_META = {
  starter: { label: "Starter", blurb: "Gantt, Kanban & core artifacts" },
  pro: { label: "Professional", blurb: "AI Copilot + all four toolkits" },
  enterprise: { label: "Enterprise", blurb: "Unlimited scale + escalations" },
};
const SLIDER_MAX = { starter: 5, pro: 25, enterprise: 100 };

function tierFromCode(code) {
  if (!code) return null;
  const t = code.split("_")[0];
  return TIER_ORDER.includes(t) ? t : null;
}

export default function SignupPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const preselect = tierFromCode(params.get("plan"));
  const [tier, setTier] = useState(preselect || "pro");
  const [billing, setBilling] = useState(params.get("plan")?.endsWith("_annual") ? "annual" : "monthly");
  const [trial, setTrial] = useState(!preselect && (params.get("plan") || "trial") === "trial");
  const [seats, setSeats] = useState(null);
  const [form, setForm] = useState({ organizationName: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);

  const checkPromo = async () => {
    if (!promo.trim()) { setPromoInfo(null); return; }
    try {
      const { data } = await api.get(`/billing/promo/${encodeURIComponent(promo.trim())}`);
      setPromoInfo({ ok: true, ...data });
    } catch { setPromoInfo({ ok: false }); }
  };

  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const byCode = useMemo(() => Object.fromEntries(plans.map((p) => [p.code, p])), [plans]);
  const code = `${tier}_${billing === "annual" ? "annual" : "monthly"}`;
  const plan = byCode[code];
  const minSeats = plan?.minSeats || 3;
  const maxSeats = SLIDER_MAX[tier];
  const effSeats = Math.min(Math.max(seats ?? minSeats, minSeats), maxSeats);
  const perSeat = plan?.price || 0;
  const total = perSeat * effSeats;

  const pickTier = (t) => { setTier(t); setTrial(false); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      login(data);
      if (!trial) {
        const { data: co } = await api.post("/billing/checkout", {
          lookup_key: code, seats: effSeats, origin_url: window.location.origin,
          promo_code: promoInfo?.ok ? promoInfo.code : undefined,
        });
        window.location.href = co.checkout_url;
        return;
      }
      navigate("/projects");
    } catch (err) { setError(errMsg(err)); setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="signup-page">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <img src="/logo-icon.png" alt="Ganttastic" className="h-9 w-9" />
          <span className="font-display font-black tracking-tighter text-lg">Ganttastic<span className="text-[#1E6FE8]">.app</span></span>
        </Link>
        <div className="overline-label mb-1">Create your organisation</div>
        <h1 className="font-display font-black text-3xl tracking-tighter mb-1">New account</h1>
        <p className="text-xs text-muted-foreground mb-5">Creates an isolated tenant workspace pre-loaded with a demo project. Paid plans go straight to Stripe checkout.</p>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Organisation name</Label><Input data-testid="signup-org-input" value={form.organizationName} onChange={set("organizationName")} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Your name</Label><Input data-testid="signup-name-input" value={form.name} onChange={set("name")} required /></div>
            <div><Label>Email</Label><Input data-testid="signup-email-input" type="email" value={form.email} onChange={set("email")} required /></div>
          </div>
          <div><Label>Password</Label><Input data-testid="signup-password-input" type="password" value={form.password} onChange={set("password")} required /></div>

          <div className="border border-border p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label>Plan</Label>
              <div className="inline-flex border border-border text-xs" role="group">
                <button type="button" data-testid="signup-billing-monthly" onClick={() => setBilling("monthly")}
                  className={`px-3 py-1.5 font-semibold ${billing === "monthly" ? "bg-[#1E6FE8] text-white" : "text-muted-foreground"}`}>Monthly</button>
                <button type="button" data-testid="signup-billing-annual" onClick={() => setBilling("annual")}
                  className={`px-3 py-1.5 font-semibold ${billing === "annual" ? "bg-[#1E6FE8] text-white" : "text-muted-foreground"}`}>Annual · save ~20%</button>
              </div>
            </div>
            <RadioGroup value={trial ? "" : tier} onValueChange={pickTier} className="space-y-2">
              {TIER_ORDER.map((t) => {
                const p = byCode[`${t}_${billing === "annual" ? "annual" : "monthly"}`];
                const active = !trial && tier === t;
                return (
                  <label key={t} data-testid={`signup-tier-${t}`}
                    className={`flex items-center gap-3 border p-3 cursor-pointer ${active ? "border-[#1E6FE8] bg-[#1E6FE8]/5" : "border-border"}`}>
                    <RadioGroupItem value={t} data-testid={`signup-tier-${t}-radio`} />
                    <span className="flex-1">
                      <span className={`block text-sm ${active ? "font-semibold" : ""}`}>{TIER_META[t].label}</span>
                      <span className="block text-xs text-muted-foreground">{TIER_META[t].blurb}</span>
                    </span>
                    <span className="text-sm font-semibold whitespace-nowrap">${p?.monthlyEq ?? "—"}<span className="text-xs font-normal text-muted-foreground">/user/mo</span></span>
                  </label>
                );
              })}
            </RadioGroup>

            {!trial && (
              <div data-testid="signup-seats-block">
                <div className="flex items-center justify-between text-sm mb-2">
                  <Label>Team size</Label>
                  <span data-testid="signup-seats-value" className="font-semibold">{effSeats} users</span>
                </div>
                <Slider data-testid="signup-seats-slider" value={[effSeats]} min={minSeats} max={maxSeats} step={1}
                  onValueChange={(v) => setSeats(v[0])} />
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>min {minSeats}</span><span>{maxSeats}{tier === "enterprise" ? "+" : ""}</span></div>
                <div data-testid="signup-price-summary" className="mt-3 border-t border-border pt-3 flex items-baseline justify-between">
                  <span className="text-xs text-muted-foreground">{effSeats} users × ${perSeat}/{billing === "annual" ? "yr" : "mo"}</span>
                  <span className="font-display font-black text-2xl tracking-tighter">${total.toLocaleString()}<span className="text-sm font-normal text-muted-foreground">/{billing === "annual" ? "yr" : "mo"}</span></span>
                </div>
              </div>
            )}

            {!trial && (
              <div data-testid="signup-promo-block">
                <div className="flex gap-2">
                  <Input data-testid="signup-promo-input" className="h-9" placeholder="Promo code (optional)"
                    value={promo} onChange={(e) => { setPromo(e.target.value); setPromoInfo(null); }} onBlur={checkPromo} />
                  <Button type="button" data-testid="signup-promo-apply-btn" variant="outline" className="rounded-none h-9" onClick={checkPromo} disabled={!promo.trim()}>Apply</Button>
                </div>
                {promoInfo?.ok && (
                  <div data-testid="signup-promo-valid" className="text-xs text-emerald-600 mt-1.5 font-semibold">
                    ✓ {promoInfo.code}: {promoInfo.percentOff ? `${promoInfo.percentOff}% off` : `$${promoInfo.amountOff} off`}
                    {promoInfo.duration === "repeating" ? ` for ${promoInfo.durationInMonths} months` : promoInfo.duration === "once" ? " (first payment)" : ""} — applied at checkout
                  </div>
                )}
                {promoInfo && !promoInfo.ok && <div data-testid="signup-promo-invalid" className="text-xs text-red-600 mt-1.5">Invalid or expired promo code</div>}
              </div>
            )}

            <button type="button" data-testid="signup-trial-toggle" onClick={() => setTrial(!trial)}
              className={`w-full border p-2.5 text-left text-sm ${trial ? "border-[#1E6FE8] bg-[#1E6FE8]/5 font-semibold" : "border-dashed border-border text-muted-foreground"}`}>
              Free 14-day trial — everything unlocked, no card required
            </button>
          </div>

          {error && <div data-testid="signup-error" className="text-sm text-red-600 border border-red-300 bg-red-50 px-3 py-2">{error}</div>}
          <Button data-testid="signup-submit-btn" type="submit" disabled={busy} className="w-full rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]">
            {busy ? "Creating…" : trial ? "Create account & start trial" : `Create account & pay $${total.toLocaleString()}/${billing === "annual" ? "yr" : "mo"}`}
          </Button>
          <div className="text-xs text-muted-foreground">Already have an account? <Link to="/login" className="underline text-[#1E6FE8]" data-testid="signup-login-link">Log in</Link></div>
        </form>
      </div>
    </div>
  );
}
